'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CustomSelect } from 'src/components/design-system/form/CustomSelect'
import Typography from 'src/components/design-system/Typography'
import { JournalArticleItem } from 'src/components/items/JournalArticleItem'
import { Pagination } from 'src/components/list/Pagination'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { journalArticleService } from 'src/_services/client/journal_article.service'
import { buildJournalArticleUrl } from 'src/_helpers/journals/buildJournalArticleUrl'
import { AHEAD_OF_PRINT_PER_PAGE } from 'src/_helpers/constants'
import { buildCurrentStateUrl } from 'src/_helpers/buildCurrentStateUrl'
import { readListQueryPage } from 'src/_helpers/listQueryParams'
import { journalService } from 'src/_services/client/journal.service'
import { journalIssueService } from 'src/_services/client/journal_issue.service'
import { authorsService } from 'src/_services/client/authors.service'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
const formatDate = (rawDate) => {
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  if (typeof rawDate === 'string') {
    const normalized = rawDate.replace(/\//g, '.').trim()
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
      return normalized
    }
  }

  return typeof rawDate === 'string' ? rawDate : null
}

async function fetchAheadOfPrints(page, sort, setPages, setCurrentAheadOfPrints, journal, locale) {
  try {
    const updatedAheadOfPrints = await journalArticleService.getAheadOfPrintByJournal(
      journal.uuid,
      page,
      AHEAD_OF_PRINT_PER_PAGE,
      sort?.value,
    )
    const orderQuery = sort?.value ? `order=${sort.value}` : undefined
    const queryParams = buildCurrentStateUrl('', page, undefined, orderQuery, locale)
    window.history.replaceState(null, '', queryParams)

    const pages = Math.ceil(updatedAheadOfPrints['hydra:totalItems'] / AHEAD_OF_PRINT_PER_PAGE)
    const enrichedItemsRaw = updatedAheadOfPrints['hydra:member']
    const enrichedItems = await Promise.all(
      enrichedItemsRaw.map(async (article) => {
        const item = article
        const journal = item?.journalUuid ? await journalService.getByUuid(item.journalUuid) : null
        const parent = item?.journalUuid ? await journalIssueService.getByUuid(item.parentUuid) : null

        const author =
          item?.authorsOpen && Array.isArray(item.authorsOpen)
            ? await Promise.all(
                item.authorsOpen.map(async (authorItem) => {
                  return authorItem?.authorUuid ? await authorsService.getByUuid(authorItem.authorUuid) : null
                }),
              ).then((authors) => authors.filter(Boolean))
            : []

        const data = { ...item, author, journal, parent }
        return { ...article, ...data }
      }),
    )
    setPages(pages)
    setCurrentAheadOfPrints(enrichedItems)
  } catch (error) {
    console.error('Error fetching ahead of print:', error)
  }
}

export default function AheadOfPrintClient({
  searchParams,
  locale,
  aheadOfPrints,
  initialOrder,
  totalItems,
  journal,
  routeContext = 'main',
}) {
  const translateEntry = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const normalizedInitialOrder = (initialOrder || searchParams.order) === 'asc' ? 'asc' : 'desc'

  const sortOptions = useMemo(
    () => [
      { value: 'desc', label: translateEntry('T_SORT_FROM_NEWEST') },
      { value: 'asc', label: translateEntry('T_SORT_FROM_OLDEST') },
    ],
    [translateEntry],
  )

  const initialSortOption = sortOptions.find((option) => option.value === normalizedInitialOrder) || sortOptions[0]

  const [sort, setSort] = useState(initialSortOption)
  const [page, setPage] = useState(readListQueryPage(searchParams, locale))
  const [currentAheadOfPrints, setCurrentAheadOfPrints] = useState(aheadOfPrints)
  const [pages, setPages] = useState(Math.ceil(totalItems / AHEAD_OF_PRINT_PER_PAGE))
  const isDomainJournal = routeContext === 'journal-domain'
  const hasAheadOfPrints = Array.isArray(currentAheadOfPrints) && currentAheadOfPrints.length > 0
  const handleSetSort = useCallback(
    (option) => {
      const nextValue = option?.target?.value ?? option?.value ?? option
      const nextOption = sortOptions.find((opt) => opt.value === nextValue)
      if (!nextOption) return
      setSort(nextOption)
      setPage(1)
    },
    [sortOptions],
  )

  const handleChangePage = (newPage) => {
    setPage(newPage)
  }

  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    fetchAheadOfPrints(page, sort, setPages, setCurrentAheadOfPrints, journal, locale)
  }, [page, sort])

  return (
    <div className="flex flex-col gap-24">
      <CustomSelect
        name="sort"
        label={translateEntry('T_SORT')}
        options={sortOptions}
        defaultValue={sort?.value}
        color={isDomainJournal ? 'journalDomain' : 'white'}
        onChange={handleSetSort}
        classNameContainer="max-w-[310px] mt-24 z-11"
      />

      {hasAheadOfPrints ? (
        <ul
          className={`list-elements grid gap-x-42 gap-y-24 t:grid-cols-2 d:grid-cols-4 border-y-1 py-16 ${
            isDomainJournal ? 'border-y-basic-gray7' : 'border-y-dark-blue-dark-blue4'
          }`}
        >
          {currentAheadOfPrints.map((el, i) => {
            const translationA = getPreferredTranslation(el, locale)
            const title = translationA?.title
            const date = formatDate(el?.publishedAt)
            const url = buildJournalArticleUrl(el, locale, routeContext, journalRoutes) || '#'

            const tags = Array.isArray(translationA?.tags)
              ? translationA.tags.map((label) => {
                  const normalized = String(label).toLowerCase()
                  const type = normalized.includes('nowość') || normalized.includes('nowosc') ? 'new' : 'default'
                  return { label, type }
                })
              : []

            const authors = (el.author || []).map((a) => a?.fullName).filter(Boolean)

            return (
              <li
                key={i}
                className={`relative after:absolute after:-right-21 after:top-0 after:hidden after:h-full after:w-1 d:after:block d:last:after:hidden ${
                  isDomainJournal ? 'after:bg-basic-gray7' : 'after:bg-dark-blue-dark-blue5'
                }`}
              >
                <JournalArticleItem
                  date={date}
                  title={title}
                  url={url}
                  tags={[
                    el.novelty && { label: translateEntry('T_NOVELTY'), type: 'new' },
                    //el.openAccess && { label: translateEntry('T_OPEN_ACCESS'), type: '' },
                    ...tags,
                  ]}
                  author={authors.join(', ')}
                  variant={isDomainJournal ? 'journal-domain' : ''}
                />
              </li>
            )
          })}
        </ul>
      ) : (
        <Typography variant="body3-normal" className="mb-32 mt-25 text-center" color="text-basic-gray2">
          {translateEntry('T_NO_RESULTS')}
        </Typography>
      )}

      {pages > 1 && <Pagination className="mb-64 mx-auto" page={page} pages={pages} setPage={handleChangePage} />}
    </div>
  )
}
